import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Flame, Lightbulb, MessageCircle, CheckCircle, Shield, Gift, ArrowRight } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
// Countdown Timer Component
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      // Get current time in Eastern Time (UTC-5)
      const now = new Date();
      const etOffset = -5 * 60; // Eastern Time offset in minutes
      const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
      const etTime = new Date(utcTime + etOffset * 60000);
      // Set target to 11:59:59 PM ET today
      const target = new Date(etTime);
      target.setHours(23, 59, 59, 0);
      // If we've passed today's deadline, set target to tomorrow
      if (etTime >= target) {
        target.setDate(target.getDate() + 1);
      }
      const diff = target.getTime() - etTime.getTime();
      if (diff > 0) {
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
        const seconds = Math.floor(diff % (1000 * 60) / 1000);
        setTimeLeft({
          hours,
          minutes,
          seconds
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  const pad = (num: number) => num.toString().padStart(2, '0');
  return <span data-id="element-2">
      La oferta termina HOY en: {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m{' '}
      {pad(timeLeft.seconds)}s
    </span>;
}
// Testimonials data with avatar URLs
const testimonials = [{
  name: 'María González',
  city: 'Ciudad de México, MX',
  review: 'Vendí mi primer PDF en 3 días. No lo podía creer. La guía te lleva de la mano paso a paso.',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face'
}, {
  name: 'Carlos Mendoza',
  city: 'Bogotá, CO',
  review: 'Pensé que era muy difícil vender online. Esta guía me demostró que solo necesitaba WhatsApp y ganas.',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face'
}, {
  name: 'Ana Lucía Vargas',
  city: 'Lima, PE',
  review: 'Ya hice 5 ventas esta semana. El bonus de las 20 ideas fue oro puro. ¡Gracias!',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
}, {
  name: 'Roberto Sánchez',
  city: 'Buenos Aires, AR',
  review: 'Sin mostrar mi cara, sin redes. Solo WhatsApp. Funciona tal cual lo explican.',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
}, {
  name: 'Valentina Reyes',
  city: 'Santiago, CL',
  review: 'Creé mi PDF en una tarde y ya tengo clientes. La inversión se pagó sola el primer día.',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop&crop=face'
}, {
  name: 'Diego Herrera',
  city: 'Medellín, CO',
  review: 'Sencillo, directo y efectivo. Ojalá hubiera encontrado esto antes.',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face'
}];
function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const cardWidth = 296; // 280px card + 16px gap
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveIndex(Math.min(newIndex, testimonials.length - 1));
    }
  };
  const scrollToIndex = (index: number) => {
    if (scrollContainerRef.current) {
      const cardWidth = 296;
      scrollContainerRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
    }
  };
  return <section className="py-12 bg-[#1f1f1f]" data-id="element-3">
      <div className="max-w-5xl mx-auto px-4" data-id="element-4">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-2xl md:text-3xl font-bold text-center mb-8" data-id="element-5">
          Lo que dicen quienes ya{' '}
          <span className="text-[#ffb700]" data-id="element-6">empezaron</span>
        </motion.h2>

        {/* Horizontal scroll container on mobile */}
        <div ref={scrollContainerRef} onScroll={handleScroll} className="flex gap-4 overflow-x-auto overflow-y-hidden pb-4 snap-x snap-mandatory md:grid md:grid-cols-3 md:overflow-visible scrollbar-hide" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        touchAction: 'pan-x'
      }} data-id="element-7">
          {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.4
        }} className="flex-shrink-0 w-[280px] md:w-auto snap-center bg-[#252525] p-6 rounded-2xl border border-[#3d3226]" data-id="element-8">
              <div className="flex items-center gap-3 mb-4" data-id="element-9">
                {/* Profile photo */}
                <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#ffb700]/30" data-id="element-10" />
                <div data-id="element-11">
                  <p className="font-bold text-white text-sm" data-id="element-12">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-13">{testimonial.city}</p>
                </div>
              </div>
              <p className="text-gray-300 text-[0.95rem] leading-relaxed" data-id="element-14">
                "{testimonial.review}"
              </p>
            </motion.div>)}
        </div>

        {/* Pagination dots - visible only on mobile */}
        <div className="flex justify-center gap-2 mt-4 md:hidden" data-id="element-15">
          {testimonials.map((_, index) => <button key={index} onClick={() => scrollToIndex(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === activeIndex ? 'bg-[#ffb700] w-6' : 'bg-gray-600 hover:bg-gray-500'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-16" />)}
        </div>
      </div>
    </section>;
}
export function LandingPage() {
  return <div className="min-h-screen w-full bg-[#1f1f1f] text-white overflow-x-hidden" data-id="element-17">
      {/* 1. HERO SECTION */}
      <section className="px-6 pt-12 pb-10 md:pt-20 md:pb-16 max-w-2xl mx-auto text-center" data-id="element-18">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-19">
          <motion.h1 variants={fadeInUp} className="text-[2.5rem] md:text-[2.8rem] font-bold leading-tight mb-4" data-id="element-20">
            Vende PDFs por WhatsApp en menos de{' '}
            <span className="text-[#ffb700]" data-id="element-21">7 Días</span>
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-[1.1rem] md:text-[1.25rem] text-gray-300 font-normal mb-8 leading-relaxed" data-id="element-22">
            Sin grabarte. Sin redes. Sin experiencia. Solo tu WhatsApp y una
            guía que te lo da todo.
          </motion.p>

          <motion.div variants={fadeInUp} className="mb-8" data-id="element-23">
            <a href="#offer" className="inline-flex items-center justify-center bg-[#ffb700] text-[#1f1f1f] font-bold text-[1.25rem] px-8 py-[16px] rounded-full shadow-[0_0_20px_rgba(255,183,0,0.3)] hover:shadow-[0_0_30px_rgba(255,183,0,0.5)] hover:scale-105 transition-all duration-300 w-full md:w-auto" data-id="element-24">
              Sí, quiero esta guía
              <ArrowRight className="ml-2 h-6 w-6" data-id="element-25" />
            </a>
          </motion.div>

          {/* Emotional Paragraph directly below button */}
          <motion.p variants={fadeInUp} className="text-[1.1rem] italic text-gray-400 leading-relaxed max-w-xl mx-auto" data-id="element-26">
            "Imagina esto: abres tu WhatsApp y ves un mensaje de alguien que
            quiere comprar tu PDF. No tienes que explicar mucho. Solo mandas un
            texto que ya tenías listo... y{' '}
            <span className="text-[#ffb700] not-italic font-bold" data-id="element-27">
              ¡pum! Venta hecha.
            </span>
            "
          </motion.p>
        </motion.div>
      </section>

      {/* 2. KEY BENEFITS */}
      <section className="px-6 py-8 max-w-5xl mx-auto" data-id="element-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-29">
          {/* Card 1 */}
          <motion.div variants={fadeInUp} className="bg-[#2a2218] p-6 rounded-2xl shadow-lg border border-[#3d3226]" data-id="element-30">
            <div className="flex items-center gap-3 mb-3" data-id="element-31">
              <Flame className="text-[#ffb700] h-6 w-6" data-id="element-32" />
              <h3 className="text-xl font-bold text-white" data-id="element-33">
                Tu primer PDF en 24h
              </h3>
            </div>
            <p className="text-gray-300 text-[1.1rem] leading-relaxed" data-id="element-34">
              Con ejemplos reales, plantillas y estructura probada. Sin escribir
              demasiado.
            </p>
          </motion.div>

          {/* Card 2 */}
          <motion.div variants={fadeInUp} className="bg-[#2a2218] p-6 rounded-2xl shadow-lg border border-[#3d3226]" data-id="element-35">
            <div className="flex items-center gap-3 mb-3" data-id="element-36">
              <Lightbulb className="text-[#ffb700] h-6 w-6" data-id="element-37" />
              <h3 className="text-xl font-bold text-white" data-id="element-38">
                Clientes desde el primer día
              </h3>
            </div>
            <p className="text-gray-300 text-[1.1rem] leading-relaxed" data-id="element-39">
              Qué decir, a quién decirlo y cómo hacer que te compren ya.
            </p>
          </motion.div>

          {/* Card 3 */}
          <motion.div variants={fadeInUp} className="bg-[#2a2218] p-6 rounded-2xl shadow-lg border border-[#3d3226]" data-id="element-40">
            <div className="flex items-center gap-3 mb-3" data-id="element-41">
              <MessageCircle className="text-[#ffb700] h-6 w-6" data-id="element-42" />
              <h3 className="text-xl font-bold text-white" data-id="element-43">
                Sin mostrar tu cara
              </h3>
            </div>
            <p className="text-gray-300 text-[1.1rem] leading-relaxed" data-id="element-44">
              Ni grabarte, ni redes. Todo se vende solo desde WhatsApp.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* 3. UNIFIED PRODUCT + BONUS BLOCK */}
      <section id="offer" className="px-4 py-10 max-w-2xl mx-auto" data-id="element-45">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-[#2a2218] rounded-3xl shadow-2xl overflow-hidden border-2 border-[#ffb700]" data-id="element-46">
          {/* Product Content */}
          <div className="p-6 md:p-10 text-center" data-id="element-47">
            {/* Product Image */}
            <div className="rounded-xl overflow-hidden shadow-lg mb-8" data-id="element-48">
              <img src="https://cdn.magicpatterns.com/uploads/abQ7h56kR1HDeVYARYbthA/ChatGPT_Image_4_feb_2026,_13_12_24.png" alt="Vende PDFs por WhatsApp en menos de 7 Días" className="w-full h-auto object-cover" data-id="element-49" />
            </div>

            <h2 className="text-2xl md:text-3xl font-bold mb-6 leading-tight" data-id="element-50">
              La Guía Definitiva para Vender en WhatsApp
            </h2>

            <div className="mb-6" data-id="element-51">
              <p className="text-gray-500 text-lg line-through font-medium inline-block mr-3" data-id="element-52">
                ~$42 USD~
              </p>
              <p className="text-5xl font-extrabold text-[#ffb700] inline-block" data-id="element-53">
                $21 USD
              </p>
            </div>

            <div className="inline-block bg-red-500/10 text-red-400 px-4 py-1 rounded-full text-sm font-bold mb-8 border border-red-500/20" data-id="element-54">
              <CountdownTimer data-id="element-55" />
            </div>

            <a href="https://infoliar.com/cart/56647203291468:1?checkout" className="block w-full bg-[#ffb700] text-[#1f1f1f] font-bold text-[1.25rem] py-[16px] rounded-full shadow-[0_0_20px_rgba(255,183,0,0.3)] hover:shadow-[0_0_30px_rgba(255,183,0,0.5)] hover:scale-[1.02] transition-all duration-300 mb-3 text-center" data-id="element-56">
              Sí, quiero empezar ahora
            </a>

            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-10" data-id="element-57">
              <Shield className="w-4 h-4" data-id="element-58" />
              <span data-id="element-59">Acceso inmediato • Pago 100% seguro</span>
            </div>

            {/* Bonus Content (Unified) */}
            <div className="border-t border-[#3d3226] pt-10" data-id="element-60">
              {/* Bonus Image */}
              <div className="rounded-xl overflow-hidden shadow-lg mb-8" data-id="element-61">
                <img src="https://cdn.magicpatterns.com/uploads/s7WSgKVdMWghuaPPYnjdny/ChatGPT_Image_4_feb_2026,_12_52_30.png" alt="20 Ideas de PDFs que Puedes Vender por WhatsApp" className="w-full h-auto object-cover" data-id="element-62" />
              </div>

              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-63">
                <Gift className="text-[#ffb700] w-6 h-6" data-id="element-64" />
                <h3 className="text-[#ffb700] font-bold text-lg tracking-wide uppercase" data-id="element-65">
                  Bonus Exclusivo Hoy
                </h3>
              </div>

              <p className="text-gray-300 mb-2" data-id="element-66">
                Al comprar esta guía, recibirás también{' '}
                <span className="text-white font-bold" data-id="element-67">GRATIS</span> un extra de
                alto valor:
              </p>

              <h4 className="text-xl font-bold text-white mb-6" data-id="element-68">
                "20 Ideas de PDFs que Puedes Vender por WhatsApp (aunque
                empieces desde cero)"
              </h4>

              <ul className="text-left space-y-4 bg-[#1f1f1f]/50 p-6 rounded-xl border border-[#3d3226]" data-id="element-69">
                {['Cómo encontrar tus primeros clientes sin redes sociales', 'Qué PDFs simples puedes vender aunque no tengas experiencia', 'Casos reales que puedes imitar ya mismo'].map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-70">
                    <CheckCircle className="w-5 h-5 text-[#ffb700] flex-shrink-0 mt-0.5" data-id="element-71" />
                    <span className="text-gray-300 text-[1.1rem]" data-id="element-72">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </motion.div>
      </section>

      {/* 4. FINAL REMINDER SECTION */}
      <section className="px-6 pt-4 pb-16 text-center max-w-2xl mx-auto" data-id="element-73">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} data-id="element-74">
          <p className="text-[1.25rem] text-gray-300 mb-8 font-medium" data-id="element-75">
            "Todo lo que necesitas para empezar hoy mismo, sin complicarte. Haz
            clic abajo para conseguirlo todo por solo $21."
          </p>

          <a href="#offer" className="inline-flex items-center justify-center bg-[#ffb700] text-[#1f1f1f] font-bold text-[1.25rem] px-10 py-[16px] rounded-full shadow-[0_0_25px_rgba(255,183,0,0.4)] hover:shadow-[0_0_40px_rgba(255,183,0,0.6)] hover:scale-105 transition-all duration-300 w-full md:w-auto" data-id="element-76">
            Sí, quiero mi acceso ahora
            <ArrowRight className="ml-2 h-6 w-6" data-id="element-77" />
          </a>
        </motion.div>
      </section>

      {/* 5. TESTIMONIALS SECTION */}
      <TestimonialsSection data-id="element-78" />

      {/* 6. FINAL CLOSING SECTION */}
      <section className="py-16 px-6 bg-[#1f1f1f]" data-id="element-79">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-2xl mx-auto text-center" data-id="element-80">
          <h2 className="text-2xl md:text-3xl font-bold mb-8" data-id="element-81">
            ¿Todavía con <span className="text-[#ffb700]" data-id="element-82">dudas</span>?
          </h2>

          <ul className="text-left space-y-4 mb-10 max-w-md mx-auto" data-id="element-83">
            {[{
            icon: '📲',
            text: 'Genera ingresos extra desde casa'
          }, {
            icon: '🚫',
            text: 'Sin necesidad de redes sociales ni seguidores'
          }, {
            icon: '✨',
            text: 'Aplica aunque nunca hayas vendido nada'
          }, {
            icon: '💬',
            text: 'Solo necesitas tu WhatsApp y este PDF'
          }, {
            icon: '⚡',
            text: 'Empieza en menos de 24 horas'
          }].map((item, index) => <li key={index} className="flex items-center gap-3 text-gray-300 text-[1.1rem]" data-id="element-84">
                <span className="text-xl" data-id="element-85">{item.icon}</span>
                <span data-id="element-86">{item.text}</span>
              </li>)}
          </ul>

          <p className="text-xl md:text-2xl font-bold text-white mb-10 leading-relaxed" data-id="element-87">
            "Tu momento puede empezar hoy. Haz clic y empieza a vender tus
            primeros PDFs en minutos."
          </p>

          <a href="#offer" className="inline-flex items-center justify-center bg-[#ffb700] text-[#1f1f1f] font-bold text-[1.25rem] px-10 py-[16px] rounded-full shadow-[0_0_25px_rgba(255,183,0,0.4)] hover:shadow-[0_0_40px_rgba(255,183,0,0.6)] hover:scale-105 transition-all duration-300 w-full md:w-auto" data-id="element-88">
            Sí, quiero empezar ahora
            <ArrowRight className="ml-2 h-6 w-6" data-id="element-89" />
          </a>
        </motion.div>
      </section>

      {/* Footer simple */}
      <footer className="py-8 text-center text-gray-600 text-sm bg-[#1a1a1a]" data-id="element-90">
        <p data-id="element-91">© 2026 Guía WhatsApp. Todos los derechos reservados.</p>
      </footer>
    </div>;
}